import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Building2, FileSignature, Package, RefreshCw, UserRound, Users } from 'lucide-react';
import { useAuth } from '@/shared/lib/auth';
import {
  maskCep,
  maskCepFromStored,
  maskCnpj,
  maskCnpjFromStored,
  maskCurrency,
  maskCurrencyFromStored,
  maskPhone,
  maskPhoneFromStored,
} from '@/shared/lib/input-masks';
import { PageHeader } from '@/shared/components/page-header';
import { MaskedInput } from '@/shared/components/masked-input';
import { Wizard, type WizardStep } from '@/shared/components/wizard';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Card } from '@/shared/ui/card';
import { Skeleton } from '@/shared/ui/skeleton';
import { Checkbox } from '@/shared/ui/checkbox';
import { Button } from '@/shared/ui/button';
import { Badge, type BadgeProps } from '@/shared/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { CreatableCombobox } from '@/shared/ui/combobox';
import { cn } from '@/shared/lib/cn';
import type { ClientContact } from '@/shared/types/client';
import { useCatalog, useCreateCatalogItem } from '@/features/catalog/api/use-catalog';
import {
  CLIENT_PROFILE_OPTIONS,
  CLIENT_SECTOR_OPTIONS,
} from '../model/client-form-meta';
import { approveClientDraft, fetchClientDetail, refreshContractStatus, saveClient } from '../api/use-clients';
import { ClientContactsSection } from './client-contacts-section';

const schema = z.object({
  name: z.string().min(1, 'Informe o nome do usuário'),
  email: z.string().email('E-mail inválido'),
  password: z.string().optional(),
  position: z.string().min(1, 'Informe o cargo'),
  name_client: z.string().min(1, 'Informe o nome do cliente'),
  cnpj: z.string().min(1, 'Informe o CNPJ'),
  email_client: z.string().email('E-mail do cliente inválido'),
  phone: z.string().optional(),
  zipcode: z.string().optional(),
  address: z.string().optional(),
  number: z.string().optional(),
  sector: z.string().optional(),
  type: z.string().optional(),
  site: z.string().optional(),
  value_pay: z.string().optional(),
  pdv: z.string().optional(),
  models: z.array(z.string()).optional(),
  lifecycleStatus: z.enum(['cliente', 'prospect']),
  clientKind: z.enum(['fixe', 'parceiro', 'whitelabel']),
});

type FormValues = z.infer<typeof schema>;

function selectOptions(base: readonly string[], current?: string) {
  if (!current || base.includes(current)) return [...base];
  return [current, ...base];
}

const CONTRACT_STATUS_LABEL: Record<string, string> = {
  pending: 'Aguardando assinatura',
  signed: 'Assinado',
  refused: 'Recusado',
  error: 'Erro ao gerar',
};

const CONTRACT_STATUS_VARIANT: Record<string, BadgeProps['variant']> = {
  pending: 'warning',
  signed: 'success',
  refused: 'destructive',
  error: 'destructive',
};

export function ClientFormPage({ embedded = false }: { embedded?: boolean } = {}) {
  const { user } = useAuth();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [refreshingContract, setRefreshingContract] = useState(false);
  const isEdit = Boolean(id);
  const [contacts, setContacts] = useState<ClientContact[]>([]);

  const { data, isLoading } = useQuery({
    queryKey: ['client', id],
    queryFn: () => fetchClientDetail(id!),
    enabled: isEdit,
  });
  const isDraft = isEdit && data?.client.registrationStatus === 'draft';

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '', email: '', password: '', position: '', name_client: '', cnpj: '',
      email_client: '', phone: '', zipcode: '', address: '', number: '', sector: '',
      type: '', site: '', value_pay: '', pdv: '', models: [], lifecycleStatus: 'prospect', clientKind: 'fixe',
    },
  });
  const errors = form.formState.errors;
  const sectorValue = form.watch('sector');
  const profileValue = form.watch('type');
  const modelsValue = form.watch('models') ?? [];

  const { data: pdvs = [], isLoading: pdvsLoading } = useCatalog('pdv');
  const createPdv = useCreateCatalogItem('pdv');
  const pdvOptions = pdvs.filter((p) => p.active).map((p) => p.name);

  const { data: modules = [], isLoading: modulesLoading } = useCatalog('module');
  const activeModuleNames = modules.filter((m) => m.active).map((m) => m.name);
  // Só módulos ativos do catálogo (/modulos). Nomes legados já salvos no
  // cliente (fora do catálogo) continuam visíveis pra não sumir a seleção.
  const moduleOptions = Array.from(new Set([...activeModuleNames, ...modelsValue]));

  useEffect(() => {
    if (!data) return;
    form.reset({
      name: data.user.profile?.name ?? '',
      // Draft público ainda não tem usuário — usa o e-mail comercial do cliente.
      email: data.user.email || data.client.email || '',
      password: '',
      position: data.user.profile?.position ?? '',
      name_client: data.client.name ?? '',
      cnpj: maskCnpjFromStored(data.client.cnpj),
      email_client: data.client.email ?? '',
      phone: maskPhoneFromStored(data.client.phone),
      zipcode: maskCepFromStored(data.client.zipcode),
      address: data.client.address ?? '',
      number: data.client.number ?? '',
      sector: data.client.sector ?? '',
      type: data.client.type ?? '',
      site: data.client.site ?? '',
      value_pay: maskCurrencyFromStored(data.client.valuePay),
      pdv: data.client.pdv ?? '',
      models: data.client.models ?? [],
      lifecycleStatus: data.client.lifecycleStatus ?? 'prospect',
      clientKind: data.client.clientKind ?? 'fixe',
    });
    setContacts(data.client.contacts ?? []);
  }, [data, form]);

  const saveMutation = useMutation({
    mutationFn: async (values: FormValues) => {
      await saveClient({
        action: isEdit ? 'edit' : 'insert',
        ...values,
        password: values.password || '********',
        contacts,
        id_user: data?.user._id || undefined,
        cpnj_verify: data?.client.cnpj,
      });
      if (isDraft && id) {
        await approveClientDraft(id);
      }
    },
    onSuccess: () => {
      toast.success(isDraft ? 'Cliente aprovado.' : isEdit ? 'Cliente atualizado.' : 'Cliente criado.');
      if (embedded && id) {
        void queryClient.invalidateQueries({ queryKey: ['client', id] });
        return;
      }
      navigate('/clientes');
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Falha ao salvar cliente.'),
  });

  if (!user || user.role !== 'Admin') {
    return <Navigate to="/login" replace />;
  }

  if (isEdit && isLoading) {
    return <Skeleton className="h-96 rounded-2xl" />;
  }

  const validateAccess = async () => {
    const ok = await form.trigger(['name', 'email', 'position']);
    if (!isEdit || isDraft) {
      const pwd = form.getValues('password');
      if (!pwd || pwd.length < 6) {
        form.setError('password', { message: 'Senha mínima de 6 caracteres' });
        return false;
      }
    }
    return ok;
  };

  const toggleModel = (product: string, checked: boolean) => {
    const current = form.getValues('models') ?? [];
    const next = checked ? [...current, product] : current.filter((item) => item !== product);
    form.setValue('models', next, { shouldDirty: true });
  };

  const steps: WizardStep[] = [
    {
      id: 'access',
      title: 'Acesso',
      icon: UserRound,
      validate: validateAccess,
      content: (
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Nome" error={errors.name?.message}>
            <Input {...form.register('name')} />
          </Field>
          <Field label="E-mail" error={errors.email?.message}>
            <Input type="email" readOnly={isEdit && !isDraft} {...form.register('email')} />
          </Field>
          <Field
            label={isEdit && !isDraft ? 'Senha (deixe vazio para manter)' : 'Senha'}
            error={errors.password?.message}
          >
            <Input type="password" {...form.register('password')} />
          </Field>
          <Field label="Cargo" error={errors.position?.message}>
            <Input {...form.register('position')} />
          </Field>
        </div>
      ),
    },
    {
      id: 'company',
      title: 'Empresa',
      icon: Building2,
      validate: () => form.trigger(['name_client', 'cnpj', 'email_client', 'lifecycleStatus', 'clientKind']),
      content: (
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Nome fantasia" error={errors.name_client?.message}>
            <Input {...form.register('name_client')} />
          </Field>
          <Field label="CNPJ" error={errors.cnpj?.message}>
            <Controller
              control={form.control}
              name="cnpj"
              render={({ field }) => (
                <MaskedInput
                  value={field.value}
                  onValueChange={field.onChange}
                  mask={maskCnpj}
                  inputMode="numeric"
                  placeholder="00.000.000/0000-00"
                />
              )}
            />
          </Field>
          <Field label="Telefone" error={errors.phone?.message}>
            <Controller
              control={form.control}
              name="phone"
              render={({ field }) => (
                <MaskedInput
                  value={field.value ?? ''}
                  onValueChange={field.onChange}
                  mask={maskPhone}
                  type="tel"
                  inputMode="tel"
                  placeholder="(00) 00000-0000"
                />
              )}
            />
          </Field>
          <Field label="CEP" error={errors.zipcode?.message}>
            <Controller
              control={form.control}
              name="zipcode"
              render={({ field }) => (
                <MaskedInput
                  value={field.value ?? ''}
                  onValueChange={field.onChange}
                  mask={maskCep}
                  inputMode="numeric"
                  placeholder="00000-000"
                />
              )}
            />
          </Field>
          <Field label="Endereço">
            <Input {...form.register('address')} />
          </Field>
          <Field label="Número">
            <Input {...form.register('number')} />
          </Field>
          <Field label="Setor" error={errors.sector?.message}>
            <Controller
              control={form.control}
              name="sector"
              render={({ field }) => (
                <Select value={field.value || undefined} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o setor" />
                  </SelectTrigger>
                  <SelectContent>
                    {selectOptions(CLIENT_SECTOR_OPTIONS, sectorValue).map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </Field>
          <Field label="Perfil" error={errors.type?.message}>
            <Controller
              control={form.control}
              name="type"
              render={({ field }) => (
                <Select value={field.value || undefined} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o perfil" />
                  </SelectTrigger>
                  <SelectContent>
                    {selectOptions(CLIENT_PROFILE_OPTIONS, profileValue).map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </Field>
          <Field label="Site">
            <Input type="url" placeholder="https://" {...form.register('site')} />
          </Field>
          <Field label="PDV" error={errors.pdv?.message}>
            <Controller
              control={form.control}
              name="pdv"
              render={({ field }) => (
                <CreatableCombobox
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  options={pdvOptions}
                  loading={pdvsLoading}
                  placeholder="Selecione o PDV"
                  emptyLabel="Nenhum PDV cadastrado."
                  onCreate={async (label) => {
                    try {
                      await createPdv(label);
                    } catch {
                      toast.error('Falha ao criar PDV.');
                      throw new Error('Falha ao criar PDV.');
                    }
                  }}
                />
              )}
            />
          </Field>
          <Field label="Status" error={errors.lifecycleStatus?.message}>
            <Controller
              control={form.control}
              name="lifecycleStatus"
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="prospect">Prospect</SelectItem>
                    <SelectItem value="cliente">Cliente</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          </Field>
          <Field label="Tipo cliente" error={errors.clientKind?.message}>
            <Controller
              control={form.control}
              name="clientKind"
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixe">Fixe</SelectItem>
                    <SelectItem value="parceiro">Parceiro</SelectItem>
                    <SelectItem value="whitelabel">Whitelabel</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          </Field>
          <Field label="E-mail comercial" className="sm:col-span-2" error={errors.email_client?.message}>
            <Input type="email" {...form.register('email_client')} />
          </Field>
          <Field label="Valor mensal" error={errors.value_pay?.message}>
            <Controller
              control={form.control}
              name="value_pay"
              render={({ field }) => (
                <div className="relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                    R$
                  </span>
                  <MaskedInput
                    className="pl-9"
                    value={field.value ?? ''}
                    onValueChange={field.onChange}
                    mask={maskCurrency}
                    inputMode="decimal"
                    placeholder="0,00"
                  />
                </div>
              )}
            />
          </Field>
        </div>
      ),
    },
    {
      id: 'products',
      title: 'Produtos',
      icon: Package,
      content: (
        <div className="space-y-3">
          {modulesLoading ? (
            <Skeleton className="h-24 rounded-xl" />
          ) : moduleOptions.length === 0 ? (
            <p className="rounded-xl border border-dashed border-border/60 px-4 py-6 text-center text-sm text-muted-foreground">
              Nenhum módulo cadastrado no catálogo ainda.
            </p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {moduleOptions.map((product) => {
                const checked = modelsValue.includes(product);
                return (
                  <label
                    key={product}
                    className="flex cursor-pointer items-center gap-3 rounded-xl border border-border/50 bg-background/40 px-3 py-2.5"
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(value) => toggleModel(product, value === true)}
                    />
                    <span className="text-sm">{product}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      ),
    },
    {
      id: 'contacts',
      title: 'Contatos da empresa',
      icon: Users,
      content: <ClientContactsSection contacts={contacts} onChange={setContacts} />,
    },
  ];

  return (
    <>
      {embedded ? null : <PageHeader title={isDraft ? 'Revisar cadastro' : isEdit ? 'Editar cliente' : 'Novo cliente'} />}
      <div className={cn(!embedded && 'mx-auto max-w-4xl')}>
      {isDraft ? (
        <div className="mb-4 rounded-2xl border border-warning/40 bg-warning/10 px-4 py-3 text-sm text-warning">
          Cadastro público aguardando aprovação. Revise os dados, defina uma senha de acesso e aprove para
          liberar o cliente e gerar o contrato.
        </div>
      ) : null}
      {isDraft && data ? (
        <Card className="mb-4 space-y-3 p-5 text-sm">
          <h2 className="text-sm font-semibold">Dados coletados no formulário público</h2>
          <div className="grid gap-x-6 gap-y-2 break-words sm:grid-cols-2">
            {data.client.legalName ? (
              <p><span className="text-muted-foreground">Razão social:</span> {data.client.legalName}</p>
            ) : null}
            {data.client.stateRegistration ? (
              <p><span className="text-muted-foreground">Inscrição estadual:</span> {data.client.stateRegistration}</p>
            ) : null}
            {(data.client.city || data.client.state) ? (
              <p>
                <span className="text-muted-foreground">Cidade/UF:</span>{' '}
                {[data.client.city, data.client.state].filter(Boolean).join(' / ')}
              </p>
            ) : null}
            {data.client.complement ? (
              <p><span className="text-muted-foreground">Complemento:</span> {data.client.complement}</p>
            ) : null}
            {data.client.whatsapp ? (
              <p><span className="text-muted-foreground">WhatsApp:</span> {data.client.whatsapp}</p>
            ) : null}
            {data.client.registrationFilledBy ? (
              <p><span className="text-muted-foreground">Preenchido por:</span> {data.client.registrationFilledBy}</p>
            ) : null}
            {data.client.billingEmails ? (
              <p className="sm:col-span-2">
                <span className="text-muted-foreground">E-mail(s) cobrança/NF:</span> {data.client.billingEmails}
              </p>
            ) : null}
            {data.client.financialContactPhone ? (
              <p><span className="text-muted-foreground">WhatsApp financeiro:</span> {data.client.financialContactPhone}</p>
            ) : null}
            {data.client.contractWitnessEmail ? (
              <p><span className="text-muted-foreground">Testemunha do contrato:</span> {data.client.contractWitnessEmail}</p>
            ) : null}
          </div>
          {data.client.contractSigners && data.client.contractSigners.length > 0 ? (
            <div>
              <p className="text-muted-foreground">Responsáveis administrativos (assinam o contrato):</p>
              <ul className="mt-1 list-disc space-y-0.5 break-words pl-5">
                {data.client.contractSigners.map((signer, i) => (
                  <li key={signer._id ?? i}>
                    {signer.name} — {signer.email}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </Card>
      ) : null}
      {data?.client.contract?.autentiqueDocumentId ? (
        <Card className="mb-4 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <FileSignature className="size-5 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Contrato — {data.client.contract.name || 'Autentique'}</p>
                <Badge
                  variant={CONTRACT_STATUS_VARIANT[data.client.contract.status ?? 'pending']}
                  className="mt-1 font-normal"
                >
                  {CONTRACT_STATUS_LABEL[data.client.contract.status ?? 'pending']}
                </Badge>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {data.client.contract.signedUrl ? (
                <Button asChild variant="secondary" size="sm">
                  <a href={data.client.contract.signedUrl} target="_blank" rel="noreferrer">
                    Ver assinado
                  </a>
                </Button>
              ) : data.client.contract.pdfUrl ? (
                <Button asChild variant="secondary" size="sm">
                  <a href={data.client.contract.pdfUrl} target="_blank" rel="noreferrer">
                    Ver PDF
                  </a>
                </Button>
              ) : null}
              <Button
                variant="secondary"
                size="sm"
                loading={refreshingContract}
                onClick={async () => {
                  if (!id) return;
                  setRefreshingContract(true);
                  try {
                    await refreshContractStatus(id);
                    await queryClient.invalidateQueries({ queryKey: ['client', id] });
                  } catch {
                    toast.error('Falha ao atualizar status do contrato.');
                  } finally {
                    setRefreshingContract(false);
                  }
                }}
              >
                <RefreshCw className="size-4" /> Atualizar status
              </Button>
            </div>
          </div>
        </Card>
      ) : null}
      <Card className="p-6">
        <Wizard
          steps={steps}
          compactStepper
          clickableStepper
          mobileOptimized
          onFinish={form.handleSubmit((v) => saveMutation.mutateAsync(v))}
          onCancel={() => navigate('/clientes')}
          isSubmitting={saveMutation.isPending}
          finishLabel={isDraft ? 'Aprovar cadastro' : isEdit ? 'Salvar' : 'Criar cliente'}
        />
      </Card>
      </div>
    </>
  );
}

function Field({
  label,
  error,
  className,
  children,
}: {
  label: string;
  error?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn('space-y-2', className)}>
      <Label>{label}</Label>
      {children}
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}
